import React, { createRef, ReactNode, useCallback, useEffect, useState } from 'react';

interface Props {
  children: ReactNode,
  caption: ReactNode,
  wide?: boolean,
}

const Tooltip = ({
  children,
  caption,
  wide,
}: Props) => {
  const [show, setShow] = useState(false);
  const [hover, setHover] = useState(false);

  const ref = createRef<HTMLDivElement>();

  const hide = useCallback((e: MouseEvent) => {
    if (ref.current && e.target && !ref.current.contains(e.target as Node)) {
      setShow(false);
    }
  }, [ref]);

  useEffect(() => {
    const body = document.querySelector('body');
    if (body) {
      body.addEventListener('click', hide);
    }
    return () => {
      if (body) {
        body.removeEventListener('click', hide);
      }
    };
  }, [hide]);

  // the bubble only exists in the dom while visible, so hidden tooltips can
  // never widen the page (nowrap captions used to cause horizontal overflow)
  const visible = show || hover;

  return (
    <div
      className="inline-flex relative"
      onClick={() => setShow(!show)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      ref={ref}
    >
      {children}
      {visible && <span
        className={[
          'bg-gray-800',
          'dark:bg-gray-100',
          'px-1',
          'text-sm',
          'text-gray-100',
          'dark:text-gray-900',
          'rounded-md',
          'absolute',
          wide ? 'whitespace-normal w-60 left-1/2 -translate-x-1/2' : 'whitespace-nowrap',
          'top-full',
          'mt-3',
          'text-center',
          'z-10',
        ].join(' ')}>
      <span
        className={[
          'absolute',
          '-top-2',
          wide ? 'left-1/2 -translate-x-1/2' : 'left-2',
          'border-b-8',
          'border-l-8',
          'border-r-8',
          'border-b-gray-800',
          'dark:border-b-gray-100',
          'border-l-transparent',
          'border-r-transparent',
          'h-0',
          'w-0'
        ].join(' ')} />
        {caption}
    </span>}
    </div>
  );
};

export default Tooltip;
